import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Grid, List, Table } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  StandardDataViewLoading,
  StandardCardLoading,
  StandardTableLoading 
} from "@/components/ui/standard-loading";

type ViewType = 'card' | 'list' | 'table';

interface DataViewScaffoldProps<T> {
  // Data
  items: T[];
  loading?: boolean;
  error?: Error | null;
  
  // Views
  views: ViewType[];
  defaultView?: ViewType;
  storageKey?: string;
  
  // Rendering
  renderCard?: (item: T, index: number) => React.ReactNode;
  renderListItem?: (item: T, index: number) => React.ReactNode;
  renderTableRow?: (item: T, index: number) => React.ReactNode;
  renderTableHeader?: () => React.ReactNode;
  
  // Layout
  cardColumns?: { sm?: number; md?: number; lg?: number; xl?: number };
  cardGap?: number;
  
  // States
  emptyState?: React.ReactNode;
  loadingState?: React.ReactNode;
  errorState?: React.ReactNode;
  
  // Interactions
  onItemClick?: (item: T) => void;
  onItemSelect?: (items: T[]) => void;
  selectable?: boolean;
}

const viewIcons = {
  card: Grid,
  list: List,
  table: Table
};

const defaultCardColumns = { sm: 1, md: 2, lg: 3, xl: 4 };

const cardVariants = {
  hidden: { opacity: 0 }, // Removed scale: 0.95
  visible: {
    opacity: 1,
    // Removed scale: 1
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }
  },
  exit: {
    opacity: 0,
    // Removed scale: 0.95
    transition: { duration: 0.2 }
  }
};

const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3 }
  }
};

// Loading skeleton components using standardized patterns

export function DataViewScaffold<T>({
  items,
  loading = false,
  error = null,
  views,
  defaultView = 'card',
  storageKey,
  renderCard,
  renderListItem,
  renderTableRow,
  renderTableHeader,
  cardColumns = defaultCardColumns,
  cardGap = 4,
  emptyState,
  loadingState,
  errorState,
  onItemClick,
  onItemSelect,
  selectable = false
}: DataViewScaffoldProps<T>) {
  const [currentView, setCurrentView] = useState<ViewType>(defaultView);
  const [selectedItems, setSelectedItems] = useState<T[]>([]);

  // Load view preference from localStorage
  useEffect(() => {
    if (storageKey) {
      const saved = localStorage.getItem(`view-${storageKey}`);
      if (saved && views.includes(saved as ViewType)) {
        setCurrentView(saved as ViewType);
      }
    }
  }, [storageKey, views]);

  // Save view preference to localStorage
  const handleViewChange = (view: ViewType) => {
    setCurrentView(view);
    if (storageKey) {
      localStorage.setItem(`view-${storageKey}`, view);
    }
  };

  // Handle item selection
  const handleItemSelect = (item: T) => {
    if (!selectable) return;
    
    const isSelected = selectedItems.includes(item);
    const newSelection = isSelected
      ? selectedItems.filter(i => i !== item)
      : [...selectedItems, item];
    
    setSelectedItems(newSelection);
    onItemSelect?.(newSelection);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (!selectable) return;
    
    const allSelected = selectedItems.length === items.length;
    const newSelection = allSelected ? [] : [...items];
    
    setSelectedItems(newSelection);
    onItemSelect?.(newSelection);
  };

  // Check if item is selected
  const isItemSelected = (item: T) => selectedItems.includes(item);

  // Generate grid classes for cards
  const getCardGridClasses = () => {
    const { sm = 1, md = 2, lg = 3, xl = 4 } = cardColumns;
    
    // Use fixed Tailwind classes to avoid dynamic class generation issues
    const gridClasses = {
      1: 'grid-cols-1',
      2: 'grid-cols-2', 
      3: 'grid-cols-3',
      4: 'grid-cols-4',
      5: 'grid-cols-5',
      6: 'grid-cols-6'
    };
    
    const gapClasses = {
      2: 'gap-2',
      3: 'gap-3',
      4: 'gap-4',
      6: 'gap-6',
      8: 'gap-8'
    };
    
    return `grid ${gridClasses[sm]} sm:${gridClasses[md]} lg:${gridClasses[lg]} xl:${gridClasses[xl]} ${gapClasses[cardGap as keyof typeof gapClasses] || 'gap-4'}`;
  };

  // Error state
  if (error && errorState) {
    return <div>{errorState}</div>;
  }

  return (
    <div className="space-y-6">
      {/* View Switcher */}
      {views.length > 1 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 bg-muted p-1 rounded-lg shadow-sm">
            {views.map((view) => {
              const Icon = viewIcons[view];
              return (
                <Button
                  key={view}
                  variant={currentView === view ? "default" : "ghost"}
                  size="sm"
                  onClick={() => handleViewChange(view)}
                  className={`h-8 w-8 p-0 transition-all duration-200 ${
                    currentView === view 
                      ? "shadow-md scale-105" 
                      : "hover:scale-105 hover:bg-accent/50"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </Button>
              );
            })}
          </div>
          
          {selectable && selectedItems.length > 0 && (
            <div className="text-sm text-muted-foreground">
              {selectedItems.length} selected
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div>
          {loadingState || (
            <StandardDataViewLoading 
              mode={currentView === 'card' ? 'cards' : currentView === 'table' ? 'table' : 'list'}
              count={8}
              hasHeader={false}
              hasFilters={false}
            />
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && items.length === 0 && emptyState && (
        <div className="flex items-center justify-center min-h-[400px]">
          {emptyState}
        </div>
      )}

      {/* Data Views */}
      {!loading && items.length > 0 && (
        <AnimatePresence mode="wait">
          <motion.div
            key={currentView}
            variants={listVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {currentView === 'card' && renderCard && (
              <div
                className="grid gap-6"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}
              >
                {items.map((item, index) => (
                  <motion.div
                    key={index}
                    variants={cardVariants}
                    onClick={() => {
                      onItemClick?.(item);
                      handleItemSelect(item);
                    }}
                    className="cursor-pointer group"
                    // Removed whileHover and whileTap scaling effects
                    // whileHover={{ 
                    //   scale: 1.02,
                    //   transition: { duration: 0.2, ease: "easeOut" }
                    // }}
                    // whileTap={{ scale: 0.98 }}
                  >
                    {renderCard(item, index)}
                  </motion.div>
                ))}
              </div>
            )}

            {currentView === 'list' && renderListItem && (
              <div className="space-y-2">
                {items.map((item, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    onClick={() => {
                      onItemClick?.(item);
                      handleItemSelect(item);
                    }}
                    className="cursor-pointer"
                  >
                    {renderListItem(item, index)}
                  </motion.div>
                ))}
              </div>
            )}

            {currentView === 'table' && renderTableRow && (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  {renderTableHeader && (
                    <thead className="bg-muted/50">
                      {renderTableHeader()}
                    </thead>
                  )}
                  <tbody>
                    {items.map((item, index) => (
                      <motion.tr
                        key={index}
                        variants={itemVariants}
                        onClick={() => {
                          onItemClick?.(item);
                          handleItemSelect(item);
                        }}
                        className={`cursor-pointer hover:bg-muted/50 transition-all duration-200 hover:shadow-sm ${
                          isItemSelected(item) ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        }`}
                      >
                        {renderTableRow(item, index)}
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}